import React, { useEffect } from 'react';

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  details?: string[];
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'danger' | 'warning' | 'info';
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  message,
  details = [],
  confirmText = 'OK',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  type = 'warning'
}) => {
  // Add animation keyframes
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes dialogFadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes dialogSlideUp {
        from {
          opacity: 0;
          transform: translateY(30px) scale(0.95);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
      @keyframes iconPulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.1); }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          iconColor: '#ef4444',
          iconBg: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.1) 100%)',
          iconBorder: 'rgba(239, 68, 68, 0.3)',
          confirmBg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          confirmShadow: 'rgba(239, 68, 68, 0.4)',
          accentColor: '#ef4444'
        };
      case 'warning':
        return {
          iconColor: '#f59e0b',
          iconBg: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.1) 100%)',
          iconBorder: 'rgba(245, 158, 11, 0.3)',
          confirmBg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          confirmShadow: 'rgba(245, 158, 11, 0.4)',
          accentColor: '#f59e0b'
        };
      default:
        return {
          iconColor: '#3b82f6',
          iconBg: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.1) 100%)',
          iconBorder: 'rgba(59, 130, 246, 0.3)',
          confirmBg: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          confirmShadow: 'rgba(59, 130, 246, 0.4)',
          accentColor: '#3b82f6'
        };
    }
  };

  const typeStyles = getTypeStyles();

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
      animation: 'dialogFadeIn 0.3s ease-out'
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #1a1f2e 0%, #0A0E17 100%)',
        borderRadius: '20px',
        padding: '2rem',
        maxWidth: '500px',
        width: '100%',
        boxShadow: `0 25px 80px rgba(0, 0, 0, 0.6), 0 0 40px ${typeStyles.confirmShadow}20`,
        border: `1px solid ${typeStyles.iconBorder}`,
        animation: 'dialogSlideUp 0.4s ease-out'
      }}>
        {/* Header with Icon */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            background: typeStyles.iconBg,
            border: `1px solid ${typeStyles.iconBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            animation: 'iconPulse 2s ease-in-out infinite'
          }}>
            <svg 
              width="28" 
              height="28" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke={typeStyles.iconColor} 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              {type === 'danger' ? (
                <>
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </>
              ) : type === 'warning' ? (
                <>
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </>
              ) : (
                <>
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8" x2="12.01" y2="8"/>
                </>
              )}
            </svg>
          </div>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            fontFamily: "'Bebas Neue', sans-serif",
            letterSpacing: '1px',
            background: `linear-gradient(135deg, ${typeStyles.iconColor} 0%, ${typeStyles.accentColor}80 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            margin: 0
          }}>
            {title}
          </h3>
        </div>

        {/* Message */}
        <div style={{ marginBottom: '1.5rem' }}>
          <p style={{
            fontSize: '1rem',
            color: 'rgba(255, 255, 255, 0.8)',
            margin: '0 0 1rem 0',
            lineHeight: '1.6',
            fontFamily: "'DM Sans', sans-serif"
          }}>
            {message}
          </p>

          {/* Details List */}
          {details.length > 0 && (
            <div style={{
              background: 'rgba(0, 0, 0, 0.3)',
              padding: '1.25rem',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <ul style={{
                margin: 0,
                paddingLeft: 0,
                listStyle: 'none'
              }}>
                {details.map((detail, index) => (
                  <li key={index} style={{
                    fontSize: '0.9rem',
                    color: 'rgba(255, 255, 255, 0.7)',
                    marginBottom: index < details.length - 1 ? '0.5rem' : 0,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    fontFamily: "'DM Sans', sans-serif"
                  }}>
                    <span style={{ 
                      color: typeStyles.iconColor, 
                      fontWeight: 'bold',
                      fontSize: '1.2rem',
                      lineHeight: '1.2'
                    }}>•</span>
                    {detail}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(255, 255, 255, 0.5)',
            margin: '1rem 0 0 0',
            fontStyle: 'italic',
            fontFamily: "'DM Sans', sans-serif"
          }}>
            This action cannot be undone.
          </p>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          paddingTop: '1.5rem'
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '0.875rem 1.75rem',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '10px',
              fontSize: '0.95rem',
              fontWeight: '600',
              color: 'rgba(255, 255, 255, 0.8)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontFamily: "'DM Sans', sans-serif"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              e.currentTarget.style.color = 'white';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
            }}
          >
            {cancelText}
          </button>
          
          <button
            onClick={onConfirm}
            style={{
              padding: '0.875rem 1.75rem',
              background: typeStyles.confirmBg,
              border: 'none',
              borderRadius: '10px',
              fontSize: '0.95rem',
              fontWeight: '700',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontFamily: "'DM Sans', sans-serif"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;